import { diningDressCodes, diningReservationPolicies, } from "./catalog.js";
import { isKnownTimeZone, isoDate, localToInstant, normalizeDateParts, normalizeTime, } from "./catalog-import-dates.js";
import { IMPORT_ASSIST_ACCEPT_BP, IMPORT_MAX_PEOPLE_PER_ITEM, IMPORT_PROPOSAL_SCHEMA_VERSION_V2, NORMALIZER_VERSION, } from "./catalog-import-review.js";
import { cleanCell, cleanText, codePointLength, nameKey } from "./catalog-import-text.js";
import { matchTaxonomyWord, normalizeCountry, normalizePeople, normalizePrice, normalizeUrl, TAXONOMY_SYNONYMS_V1, } from "./catalog-import-values.js";
import { sha256HexOfText } from "./sha256.js";
/**
 * The format of a Google Place ID cell: URL-safe text, 16 to 255 characters (`PLACE_ID_MAX`). Real ids are
 * about 27 characters or more; the lower bound is loose on purpose. The value is a key for a lookup and is
 * never fetched or shown as a link.
 */
const PLACE_ID_FORMAT = /^[A-Za-z0-9_-]{16,255}$/;
/** The key of an assistant answer: the field and the tidy word. */
export const assistKey = (fieldCode, word) => `${fieldCode}\u0000${word}`;
/**
 * City to time zone for the markets Lafayette serves (version 1). The key is `nameKey(city)`. A zone from
 * this table is only a SUGGESTION: the row gets `TIME_ZONE_INFERRED` and an editor must confirm it.
 */
export const CITY_TIME_ZONES_V1 = (() => {
    const table = {};
    const add = (timeZone, countryCode, ...cities) => {
        for (const city of cities)
            table[nameKey(city)] = { timeZone, countryCode };
    };
    add("Asia/Kolkata", "IN", "Mumbai", "Bombay", "Delhi", "New Delhi", "Bengaluru", "Bangalore", "Goa", "Chennai", "Kolkata", "Hyderabad", "Pune", "Jaipur", "Udaipur", "Kochi", "Ahmedabad");
    add("Asia/Singapore", "SG", "Singapore");
    add("Asia/Dubai", "AE", "Dubai", "Abu Dhabi");
    add("Asia/Tokyo", "JP", "Tokyo", "Kyoto", "Osaka");
    add("Europe/Paris", "FR", "Paris", "Nice", "Cannes", "Lyon");
    add("Europe/London", "GB", "London");
    add("America/New_York", "US", "New York", "New York City");
    add("Asia/Colombo", "LK", "Colombo");
    add("Indian/Maldives", "MV", "Male");
    return Object.freeze(table);
})();
// --- keys ------------------------------------------------------------------------------------------------
/** The canonical text a row fingerprint hashes: the normalizer version, the kind, the mapped cleaned cells. */
export function rowFingerprintText(importKind, fields) {
    const entries = [];
    for (const [field, cells] of Object.entries(fields)) {
        cells.forEach((cell, ordinal) => {
            const text = cleanText(cell.text);
            if (text !== "")
                entries.push([field, ordinal, text]);
        });
    }
    entries.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] - b[1]));
    return JSON.stringify([NORMALIZER_VERSION, importKind, entries]);
}
/**
 * SHA-256 of the canonical mapped cells of a row. It does not depend on the row number, the file name,
 * the column order or the columns staff ignored, so the same data in another file has the same value.
 */
export function rowFingerprint(importKind, fields) {
    return sha256HexOfText(rowFingerprintText(importKind, fields));
}
/**
 * The key of the row in its source system: the mapped `source_key` cell (tidy, upper case, leading zeros of a
 * number removed), else `row:` and 32 hex characters of the fingerprint.
 */
export function sourceKeyOf(cell, fingerprint) {
    if (cell !== null) {
        const upper = cell.toUpperCase();
        const key = /^\d+$/.test(upper) ? upper.replace(/^0+(?=\d)/, "") : upper;
        if (key.length <= 255)
            return { key, derived: false };
    }
    return { key: `row:${fingerprint.slice(0, 32)}`, derived: true };
}
// --- the row ------------------------------------------------------------------------------------------------
const MAX_PROPOSAL_BYTES = 15_000;
const TEXT_LIMITS = {
    name: 200,
    short_description: 300,
    description: 8000,
    address_text: 300,
    location_city: 100,
    region: 100,
    venue_name: 200,
    service_area_text: 300,
};
const NUMBER_FIELDS = {
    VILLA: [
        ["bedrooms", 0, 50],
        ["bathrooms", 0, 50],
        ["max_guests", 1, 100],
        ["minimum_stay_nights", 1, 365],
    ],
    EXPERIENCE: [
        ["duration_minutes", 1, 10_080],
        ["group_min", 1, 500],
        ["group_max", 1, 500],
        ["minimum_age", 0, 120],
    ],
    SERVICE: [["lead_time_hours", 0, 8760]],
};
/** The target fields that belong to one item type only. A value in another type's field is ignored with a warning. */
const TYPE_FIELDS = {
    EVENT: [
        "venue_name",
        "start_date",
        "start_day",
        "start_month",
        "start_year",
        "start_time",
        "end_date",
        "end_time",
        "performers",
    ],
    DINING: ["cuisine", "dress_code", "reservation_policy"],
    VILLA: ["bedrooms", "bathrooms", "max_guests", "minimum_stay_nights"],
    EXPERIENCE: ["duration_minutes", "group_min", "group_max", "minimum_age"],
    SERVICE: ["service_area_text", "lead_time_hours"],
};
const ALL_TYPE_FIELDS = new Set(Object.values(TYPE_FIELDS).flat());
/** The fields whose value is a code from a list: a word that fits none needs a staff decision. */
export const TAXONOMY_FIELD_CODES = Object.freeze([
    "category",
    "cuisine",
    "dress_code",
    "reservation_policy",
]);
const titleOf = (code) => code
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
/** The codes of the enumerated dining fields, with labels, for the decisions that choose one. */
export const ENUM_TAXONOMY_OPTIONS = {
    dress_code: diningDressCodes.map((code) => ({ code, label: titleOf(code) })),
    reservation_policy: diningReservationPolicies.map((code) => ({ code, label: titleOf(code) })),
};
const jsonBytes = (value) => new TextEncoder().encode(JSON.stringify(value)).length;
/**
 * Normalizes one row. The order is fixed: item type, name, location, price, link, taxonomy, then the
 * fields of the item type. A row with an ERROR issue is INVALID. A row with only WARNING issues is
 * importable; the issues that need a staff choice become required decisions (`requiredDecisionsOf`).
 */
export function normalizeImportRow(input) {
    const issues = [];
    const rules = {};
    const assistNeeds = [];
    const assistRefs = [];
    const fingerprint = rowFingerprint("CATALOG_ITEMS", input.fields);
    const add = (issue) => {
        const fieldCode = issue.fieldCode ?? null;
        if (!issues.some((seen) => seen.code === issue.code && seen.fieldCode === fieldCode)) {
            issues.push({ severity: issue.severity, code: issue.code, fieldCode });
        }
    };
    const issue = (severity, code, fieldCode) => add({ severity, code, fieldCode });
    const cellsOf = (field) => input.fields[field] ?? [];
    const first = (field, multiline = false) => {
        for (const cell of cellsOf(field)) {
            const text = cleanCell(cell.text, multiline);
            if (text !== null)
                return text;
        }
        return null;
    };
    const bounded = (field, multiline = false) => {
        const text = first(field, multiline);
        if (text !== null && codePointLength(text) > TEXT_LIMITS[field]) {
            issue("ERROR", "OUT_OF_RANGE", field);
            return null;
        }
        if (text !== null)
            rules[field] = { rule: "CLEANED", source: "CELL" };
        return text;
    };
    const itemType = input.itemType;
    if (itemType === null) {
        issue("ERROR", "REQUIRED_FIELD_MISSING", "item_type");
        return { proposal: null, issues, assistNeeds, fingerprint };
    }
    const name = first("name");
    if (name === null)
        issue("ERROR", "REQUIRED_FIELD_MISSING", "name");
    else if (codePointLength(name) > TEXT_LIMITS.name)
        issue("ERROR", "OUT_OF_RANGE", "name");
    rules.name = { rule: "CLEANED", source: "CELL" };
    // Values of another item type's fields are ignored, never silently used.
    for (const field of ALL_TYPE_FIELDS) {
        if (!TYPE_FIELDS[itemType].includes(field) && first(field) !== null) {
            issue("WARNING", "UNSUPPORTED_VALUE", field);
        }
    }
    const shortDescription = bounded("short_description");
    const description = bounded("description", true);
    const addressText = bounded("address_text");
    const locality = (() => {
        const text = first("location_city");
        if (text !== null && codePointLength(text) > TEXT_LIMITS.location_city) {
            issue("ERROR", "OUT_OF_RANGE", "location_city");
            return null;
        }
        if (text !== null)
            rules.location_city = { rule: "CLEANED", source: "CELL" };
        return text;
    })();
    const region = bounded("region");
    const country = normalizeCountry(first("country_code"));
    for (const found of country.issues)
        add(found);
    if (country.code !== null)
        rules.country_code = { rule: "COUNTRY_LOOKUP", source: "CELL" };
    const latText = first("latitude");
    const lngText = first("longitude");
    let latitude = null;
    let longitude = null;
    if (latText !== null || lngText !== null) {
        const lat = latText !== null && /^-?\d{1,2}(\.\d+)?$/.test(latText) ? Number(latText) : null;
        const lng = lngText !== null && /^-?\d{1,3}(\.\d+)?$/.test(lngText) ? Number(lngText) : null;
        if (latText === null || lngText === null) {
            issue("ERROR", "REQUIRED_FIELD_MISSING", latText === null ? "latitude" : "longitude");
        }
        else if (lat === null || lng === null) {
            issue("ERROR", "INVALID_FORMAT", lat === null ? "latitude" : "longitude");
        }
        else if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            issue("ERROR", "OUT_OF_RANGE", lat < -90 || lat > 90 ? "latitude" : "longitude");
        }
        else {
            latitude = lat;
            longitude = lng;
            rules.latitude = { rule: "PARSED", source: "CELL" };
            rules.longitude = { rule: "PARSED", source: "CELL" };
        }
    }
    // An explicit item-level zone (a column). Events also read it as the zone of the schedule.
    const zoneCell = first("time_zone");
    let zoneColumn = null;
    if (zoneCell !== null) {
        if (isKnownTimeZone(zoneCell))
            zoneColumn = zoneCell;
        else
            issue("ERROR", "INVALID_TIME_ZONE", "time_zone");
    }
    const priceResult = normalizePrice(first("price_text"), first("price_currency"), itemType);
    for (const found of priceResult.issues)
        add(found);
    if (priceResult.price !== null) {
        rules.price_text = {
            rule: priceResult.currencyRule ?? "PARSED",
            source: priceResult.currencyRule === "CURRENCY_COLUMN" ? "CELLS" : "CELL",
        };
        if (priceResult.unitDefaulted && priceResult.price.unit !== null) {
            rules.price_unit = { rule: "DEFAULT", source: "ITEM_TYPE_DEFAULT" };
        }
    }
    const urlResult = normalizeUrl(first("external_url"));
    for (const found of urlResult.issues)
        add(found);
    if (urlResult.url !== null)
        rules.external_url = { rule: "URL_NORMALIZED", source: "CELL" };
    // --- words that must be codes -----------------------------------------------------------------------------
    const taxonomy = (fieldCode, word, options) => {
        const found = matchTaxonomyWord(word, options, TAXONOMY_SYNONYMS_V1[fieldCode] ?? {});
        if (found.kind === "match") {
            rules[fieldCode] = { rule: found.rule, source: "CELL" };
            return found.code;
        }
        const answer = input.assists.get(assistKey(fieldCode, word));
        if (answer === undefined) {
            issue("WARNING", "UNKNOWN_CODE", fieldCode);
            if (input.assistEnabled)
                assistNeeds.push({ fieldCode, word, allowed: options });
            return null;
        }
        // This row used the answer for its word (accepted or not): the review shows it on this row.
        const inputSha256 = sha256HexOfText(word);
        if (!assistRefs.some((ref) => ref.fieldCode === fieldCode && ref.inputSha256 === inputSha256)) {
            assistRefs.push({ fieldCode, inputSha256 });
        }
        const allowed = answer.choice !== null && options.some((option) => option.code === answer.choice);
        if (allowed && answer.confidenceBp >= IMPORT_ASSIST_ACCEPT_BP) {
            issue("WARNING", "MODEL_ASSISTED", fieldCode);
            rules[fieldCode] = { rule: "MODEL_ASSISTED", source: "MODEL" };
            return answer.choice;
        }
        issue("WARNING", "UNKNOWN_CODE", fieldCode);
        issue("WARNING", "AMBIGUOUS_VALUE", fieldCode);
        return null;
    };
    const categoryWord = first("category");
    const category = categoryWord === null ? null : taxonomy("category", categoryWord, input.taxonomy.categories);
    // --- the fields of the item type ---------------------------------------------------------------------------
    const subtype = {};
    const tags = [];
    let schedule = null;
    let people = [];
    if (itemType === "EVENT") {
        const venue = bounded("venue_name");
        if (venue !== null)
            subtype.venue_name = venue;
        const dateField = cellsOf("start_date")[0];
        const dateInput = {
            date: first("start_date"),
            day: first("start_day"),
            month: first("start_month"),
            year: first("start_year"),
            serial: dateField !== undefined && input.dateColumns.includes(dateField.columnIndex),
            date1904: input.date1904,
            dateOrder: input.run.dateOrder,
        };
        const startDate = normalizeDateParts(dateInput);
        let start = null;
        if (!startDate.ok) {
            if ("missing" in startDate)
                issue("ERROR", "REQUIRED_FIELD_MISSING", "start_date");
            else
                issue(startDate.severity, startDate.code, "start_date");
        }
        else {
            start = startDate.value.date;
            rules.start_date = startDate.value.joined
                ? { rule: "DATE_PARTS_JOINED", source: "CELLS" }
                : { rule: "PARSED", source: "CELL" };
        }
        const startTime = normalizeTime(first("start_time"));
        let startMinutes = null;
        let rangeEnd = null;
        if (startTime.kind === "issue")
            issue(startTime.severity, startTime.code, "start_time");
        else if (startTime.kind === "time")
            startMinutes = startTime.minutes;
        else if (startTime.kind === "range") {
            startMinutes = startTime.start;
            rangeEnd = startTime.end;
        }
        if (startMinutes !== null)
            rules.start_time = { rule: "PARSED", source: "CELL" };
        const endTime = normalizeTime(first("end_time"));
        let endMinutes = null;
        if (endTime.kind === "issue")
            issue(endTime.severity, endTime.code, "end_time");
        else if (endTime.kind === "time")
            endMinutes = endTime.minutes;
        else if (endTime.kind === "range")
            issue("ERROR", "INVALID_FORMAT", "end_time");
        if (endMinutes === null)
            endMinutes = rangeEnd;
        const endDateText = first("end_date");
        let endDate = null;
        if (endDateText !== null) {
            const parsedEnd = normalizeDateParts({
                ...dateInput,
                date: endDateText,
                day: null,
                month: null,
                serial: cellsOf("end_date")[0] !== undefined &&
                    input.dateColumns.includes(cellsOf("end_date")[0].columnIndex),
                year: dateInput.year,
            });
            if (!parsedEnd.ok) {
                if (!("missing" in parsedEnd))
                    issue(parsedEnd.severity, parsedEnd.code, "end_date");
            }
            else
                endDate = parsedEnd.value.date;
        }
        // The zone: a column, the run setting, or the city (a suggestion).
        let zone = null;
        let zoneSource = null;
        if (zoneColumn !== null) {
            zone = zoneColumn;
            zoneSource = "COLUMN";
        }
        else if (zoneCell === null) {
            if (input.run.defaultTimeZone !== null && isKnownTimeZone(input.run.defaultTimeZone)) {
                zone = input.run.defaultTimeZone;
                zoneSource = "JOB_DEFAULT";
            }
            else {
                const city = locality === null ? undefined : CITY_TIME_ZONES_V1[nameKey(locality)];
                if (city !== undefined) {
                    zone = city.timeZone;
                    zoneSource = "CITY_LOOKUP";
                    issue("WARNING", "TIME_ZONE_INFERRED", "time_zone");
                }
                else {
                    issue("ERROR", "INVALID_TIME_ZONE", "time_zone");
                }
            }
        }
        if (zoneSource !== null) {
            rules.time_zone = {
                rule: zoneSource === "CITY_LOOKUP" ? "COUNTRY_LOOKUP" : "DEFAULT",
                source: zoneSource === "COLUMN"
                    ? "CELL"
                    : zoneSource === "JOB_DEFAULT"
                        ? "JOB_SETTING"
                        : "CITY_LOOKUP",
            };
            if (zoneSource === "COLUMN")
                rules.time_zone = { rule: "CLEANED", source: "CELL" };
        }
        if (start !== null && zone !== null && zoneSource !== null) {
            const exact = startMinutes !== null;
            const startAt = localToInstant(start, startMinutes ?? 0, zone);
            if (startAt.kind === "gap")
                issue("ERROR", "OUT_OF_RANGE", "start_time");
            else {
                if (startAt.overlap)
                    issue("WARNING", "AMBIGUOUS_VALUE", "start_time");
                let endsAt = null;
                if (endDate !== null || (exact && endMinutes !== null)) {
                    if (!exact && endMinutes !== null) {
                        issue("ERROR", "INVALID_INTERVAL", "end_time");
                    }
                    else {
                        let endLocalDate = endDate ?? start;
                        if (endDate === null &&
                            endMinutes !== null &&
                            startMinutes !== null &&
                            endMinutes < startMinutes) {
                            const next = new Date(Date.UTC(start.year, start.month - 1, start.day + 1));
                            endLocalDate = {
                                year: next.getUTCFullYear(),
                                month: next.getUTCMonth() + 1,
                                day: next.getUTCDate(),
                            };
                        }
                        const endAt = localToInstant(endLocalDate, exact ? (endMinutes ?? 0) : 0, zone);
                        if (endAt.kind === "gap")
                            issue("ERROR", "OUT_OF_RANGE", "end_time");
                        else if (endAt.instant.getTime() < startAt.instant.getTime()) {
                            issue("ERROR", "INVALID_INTERVAL", "end_date");
                        }
                        else
                            endsAt = endAt.instant;
                    }
                }
                schedule = {
                    startsAt: startAt.instant.toISOString(),
                    endsAt: endsAt === null ? null : endsAt.toISOString(),
                    timeZone: zone,
                    precision: exact ? "EXACT" : "DATE_ONLY",
                    zoneSource,
                };
            }
        }
        const performerCells = cellsOf("performers").map((cell) => cleanCell(cell.text));
        const listed = normalizePeople(performerCells, cellsOf("performers").length === 1);
        if (listed.tooLong)
            issue("ERROR", "OUT_OF_RANGE", "performers");
        if (listed.people.length > IMPORT_MAX_PEOPLE_PER_ITEM)
            issue("ERROR", "OUT_OF_RANGE", "performers");
        people = listed.people.slice(0, IMPORT_MAX_PEOPLE_PER_ITEM);
        if (people.length > 0) {
            rules.performers = {
                rule: cellsOf("performers").length === 1 ? "SPLIT_LIST" : "CLEANED",
                source: cellsOf("performers").length === 1 ? "CELL" : "CELLS",
            };
        }
    }
    else if (itemType === "DINING") {
        const seen = new Set();
        for (const cell of cellsOf("cuisine")) {
            const text = cleanCell(cell.text);
            if (text === null)
                continue;
            for (const word of text.split(/\s*[,;]\s*/)) {
                if (word === "")
                    continue;
                const code = taxonomy("cuisine", word, input.taxonomy.cuisineTags);
                if (code !== null && !seen.has(code)) {
                    seen.add(code);
                    tags.push({ namespace: "CUISINE", code });
                }
            }
        }
        for (const field of ["dress_code", "reservation_policy"]) {
            const word = first(field);
            if (word === null)
                continue;
            const code = taxonomy(field, word, ENUM_TAXONOMY_OPTIONS[field]);
            if (code !== null)
                subtype[field] = code;
        }
    }
    else if (itemType === "SERVICE") {
        const area = bounded("service_area_text");
        if (area !== null)
            subtype.service_area_text = area;
    }
    for (const [field, min, max] of NUMBER_FIELDS[itemType] ?? []) {
        const text = first(field);
        if (text === null)
            continue;
        if (!/^\d{1,6}$/.test(text)) {
            issue("ERROR", "INVALID_FORMAT", field);
            continue;
        }
        const value = Number(text);
        if (value < min || value > max)
            issue("ERROR", "OUT_OF_RANGE", field);
        else {
            subtype[field] = value;
            rules[field] = { rule: "PARSED", source: "CELL" };
        }
    }
    if (typeof subtype.group_min === "number" &&
        typeof subtype.group_max === "number" &&
        subtype.group_min > subtype.group_max) {
        issue("ERROR", "INVALID_INTERVAL", "group_min");
    }
    // A Google Place ID is evidence, not content: a bad value is dropped with a warning and never blocks the row.
    const placeCell = first("google_place_id");
    let placeId = null;
    if (placeCell !== null) {
        if (PLACE_ID_FORMAT.test(placeCell))
            placeId = placeCell;
        else
            issue("WARNING", "PLACE_ID_INVALID", "google_place_id");
    }
    const sourceCell = first("source_key");
    const key = sourceKeyOf(sourceCell, fingerprint);
    if (sourceCell !== null && key.derived)
        issue("ERROR", "OUT_OF_RANGE", "source_key");
    if (name === null)
        return { proposal: null, issues, assistNeeds, fingerprint };
    const priceOut = priceResult.price;
    let proposal = {
        version: IMPORT_PROPOSAL_SCHEMA_VERSION_V2,
        normalizerVersion: NORMALIZER_VERSION,
        itemType,
        name,
        shortDescription,
        description,
        addressText,
        locality,
        region,
        countryCode: country.code,
        latitude,
        longitude,
        timeZone: zoneColumn,
        externalUrl: urlResult.url,
        placeId,
        category,
        tags,
        price: priceOut,
        schedule,
        subtype,
        people,
        source: { system: input.run.sourceSystem, key: key.key, keyDerived: key.derived, fingerprint },
        fieldRules: rules,
        ...(assistRefs.length === 0 ? {} : { assistRefs }),
    };
    if (jsonBytes(proposal) > MAX_PROPOSAL_BYTES && proposal.description !== null) {
        // The raw cell keeps the full text; the proposal drops the description before it breaks the size limit.
        proposal = { ...proposal, description: null };
        issue("WARNING", "OUT_OF_RANGE", "description");
    }
    if (jsonBytes(proposal) > MAX_PROPOSAL_BYTES)
        issue("ERROR", "OUT_OF_RANGE", "name");
    return { proposal, issues, assistNeeds, fingerprint };
}
/** The date of a proposal's schedule as `YYYY-MM-DD` in its own zone. Two events on one day share it. */
export function scheduleLocalDate(schedule) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: schedule.timeZone,
        calendar: "gregory",
        numberingSystem: "latn",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date(schedule.startsAt));
    const part = (type) => Number(parts.find((entry) => entry.type === type)?.value ?? "0");
    return isoDate({ year: part("year"), month: part("month"), day: part("day") });
}
